import { Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
export const roofEvidenceClaims = [
    "asbestos_documented",
    "asbestos_suspected",
    "fibrocement_composition_unknown",
    "fibrocement_asbestos_free_documented"
];
export const roofEvidenceMethods = [
    "documentation",
    "inspection",
    "inventory",
    "public_remote_sensing",
    "model_visual"
];
export const roofRemediationStates = [
    "not_remediated",
    "partial",
    "complete",
    "in_progress",
    "unknown"
];
const claims = Type.Union([
    Type.Literal("asbestos_documented"),
    Type.Literal("asbestos_suspected"),
    Type.Literal("fibrocement_composition_unknown"),
    Type.Literal("fibrocement_asbestos_free_documented")
]);
const methods = Type.Union([
    Type.Literal("documentation"),
    Type.Literal("inspection"),
    Type.Literal("inventory"),
    Type.Literal("public_remote_sensing"),
    Type.Literal("model_visual")
]);
const states = Type.Union([
    Type.Literal("not_remediated"),
    Type.Literal("partial"),
    Type.Literal("complete"),
    Type.Literal("in_progress"),
    Type.Literal("unknown")
]);
const common = {
    version: Type.Optional(Type.Literal(1)),
    conflicts: Type.Optional(Type.Union([
        Type.Literal("exclude"),
        Type.Literal("include_for_review"),
        Type.Literal("only_for_review")
    ])),
    association: Type.Optional(Type.Literal("building_supported"))
};
export const roofEvidenceFilterSchema = Type.Union([
    Type.Object({
        ...common,
        preset: Type.Literal("asbestos_remediation"),
        includeFibrocementUnknown: Type.Optional(Type.Boolean()),
        includeRemoteSuspicions: Type.Optional(Type.Boolean()),
        includeUnknownRemediation: Type.Optional(Type.Boolean())
    }, { additionalProperties: false }),
    Type.Object({
        ...common,
        preset: Type.Literal("custom"),
        claims: Type.Array(claims, { minItems: 1, maxItems: 4, uniqueItems: true }),
        methods: Type.Optional(Type.Array(methods, { maxItems: 5, uniqueItems: true })),
        remediationStates: Type.Optional(Type.Array(states, { maxItems: 5, uniqueItems: true })),
        sourceIds: Type.Optional(Type.Array(Type.String({ pattern: "^[a-z0-9_-]{1,80}$" }), {
            maxItems: 30,
            uniqueItems: true
        })),
        observedFrom: Type.Optional(Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" })),
        observedTo: Type.Optional(Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" })),
        includeUndated: Type.Optional(Type.Boolean())
    }, { additionalProperties: false })
]);
export function normalizeRoofEvidenceFilter(value) {
    if (!Value.Check(roofEvidenceFilterSchema, value))
        throw Object.assign(new Error("Filtro materiali non valido"), { statusCode: 400 });
    const base = {
        version: 1,
        conflicts: value.conflicts ?? "exclude",
        association: "building_supported"
    };
    if (value.preset === "asbestos_remediation")
        return {
            ...base,
            preset: value.preset,
            includeFibrocementUnknown: value.includeFibrocementUnknown ?? false,
            includeRemoteSuspicions: value.includeRemoteSuspicions ?? true,
            includeUnknownRemediation: value.includeUnknownRemediation ?? true
        };
    for (const date of [value.observedFrom, value.observedTo])
        if (date &&
            (Number.isNaN(Date.parse(date)) || new Date(date).toISOString().slice(0, 10) !== date))
            throw Object.assign(new Error("Data osservazione non valida"), { statusCode: 400 });
    if (value.observedFrom && value.observedTo && value.observedFrom > value.observedTo)
        throw Object.assign(new Error("Intervallo osservazioni non valido"), { statusCode: 400 });
    return {
        ...base,
        preset: "custom",
        claims: roofEvidenceClaims.filter((v) => value.claims.includes(v)),
        ...(value.methods?.length
            ? { methods: roofEvidenceMethods.filter((v) => value.methods?.includes(v)) }
            : {}),
        ...(value.remediationStates?.length
            ? {
                remediationStates: roofRemediationStates.filter((v) => value.remediationStates?.includes(v))
            }
            : {}),
        ...(value.sourceIds?.length ? { sourceIds: [...value.sourceIds].sort() } : {}),
        ...(value.observedFrom ? { observedFrom: value.observedFrom } : {}),
        ...(value.observedTo ? { observedTo: value.observedTo } : {}),
        includeUndated: value.includeUndated ?? true
    };
}
export function parseRoofEvidenceFilter(value) {
    if (value === undefined)
        return undefined;
    if (value.length > 6000)
        throw Object.assign(new Error("Filtro materiali troppo lungo"), { statusCode: 400 });
    try {
        return normalizeRoofEvidenceFilter(JSON.parse(value));
    }
    catch {
        throw Object.assign(new Error("Filtro materiali non valido"), { statusCode: 400 });
    }
}
export function serializeRoofEvidenceFilter(value) {
    return value === undefined ? undefined : JSON.stringify(normalizeRoofEvidenceFilter(value));
}
const textOrNull = Type.Union([Type.String(), Type.Null()]);
export const roofEvidenceSummarySchema = Type.Object({
    operatorCorrections: Type.Optional(Type.Array(Type.Object({
        kind: Type.Union([Type.Literal("building"), Type.Literal("source_record")]),
        id: Type.String(),
        version: Type.Integer(),
        remediationState: Type.Union([Type.Literal("complete"), Type.Null()]),
        linkedBuildingId: textOrNull,
        note: Type.String(),
        updatedAt: Type.String()
    }))),
    snapshotId: textOrNull,
    evidenceIds: Type.Array(Type.String()),
    claims: Type.Array(claims),
    remediationStates: Type.Array(states),
    sourceIds: Type.Array(Type.String()),
    conflicting: Type.Boolean(),
    matchesQuery: Type.Boolean(),
    remediationEligibility: Type.Union([
        Type.Literal("eligible"),
        Type.Literal("review_only"),
        Type.Literal("ineligible")
    ]),
    reason: Type.String()
}, { additionalProperties: false });
export const roofEvidenceCoverageSchema = Type.Object({
    snapshotId: textOrNull,
    availability: Type.Union([
        Type.Literal("available"),
        Type.Literal("partial"),
        Type.Literal("unavailable")
    ]),
    completeness: Type.Literal("unknown"),
    sourcesChecked: Type.Integer({ minimum: 0 }),
    sources: Type.Optional(Type.Array(Type.Object({ id: Type.String(), name: Type.String() }, { additionalProperties: false }))),
    unassociatedObservationCount: Type.Integer({ minimum: 0 })
}, { additionalProperties: false });
export const roofEvidenceDetailSchema = Type.Object({
    summary: roofEvidenceSummarySchema,
    observations: Type.Array(Type.Object({
        id: Type.String(),
        sourceId: Type.String(),
        sourceName: Type.String(),
        sourceUrl: Type.String(),
        license: Type.String(),
        claim: claims,
        method: methods,
        remediationState: states,
        observedAt: textOrNull,
        dateQuality: Type.String(),
        portionKey: Type.String(),
        association: Type.String(),
        sourceRecordId: Type.String()
    }, { additionalProperties: false })),
    hasMore: Type.Boolean()
}, { additionalProperties: false });
//# sourceMappingURL=roof-evidence.js.map